import {useInfiniteQuery} from '@tanstack/react-query';
import {apiClient} from '@common/http/query-client';
import {BackendResponse} from '@common/http/backend-response/backend-response';

interface License {
  id: number;
  key: string;
  package_id: number;
  package_name?: string;
  active_data?: any;
  assigned_user?: any;
  can_assign_date?: string;
}

interface FetchLicensesResponse extends BackendResponse {
  data: License[];
  isdone: number;
}

interface UseLicensesParams {
  filter?: string;
}

export function useLicenses(params: UseLicensesParams = {}) {
  return useInfiniteQuery({
    queryKey: ['licenses', params],
    queryFn: ({pageParam = 1}) => fetchLicenses({...params, page: pageParam}),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages, lastPageParam) => {
      if (lastPage.isdone === 1) {
        return undefined;
      }
      return lastPageParam + 1;
    },
    select: data => ({
      ...data,
      licenses: data.pages.flatMap(page => page.data),
    }),
  });
}

async function fetchLicenses(
  params: UseLicensesParams & {page?: number},
): Promise<FetchLicensesResponse> {
  // Generate a token first
  const tokenResponse = await apiClient.post('drive/generate-token', {
    endpoint_type: 'licenses',
  });

  const token = tokenResponse.data.token;

  // Call external API directly with axios
  const response = await fetch('https://apcrosa.syscomlive.co.uk/api/v1/drive/license/get-my-license', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-API-Token': token,
    },
    body: JSON.stringify({
      filter: params.filter || '0',
      page: params.page || 1,
      use_for_drive: true,
    }),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.json();
}
